import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Eye, Monitor, Pencil, Search, Smartphone, Trash2, X } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Pill } from "../Common";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { SiteRenderer } from "./SiteRenderer";
import { CATEGORIES } from "./blocks";

export function TemplateThumb({ t, width = 1280 }) {
  const ref = useRef(null);
  const frame = useRef(null);
  const [scale, setScale] = useState(0.25);
  const [body, setBody] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) { return undefined; }
    const ro = new ResizeObserver(() => setScale(el.clientWidth / width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);
  useEffect(() => {
    const doc = frame.current?.contentDocument;
    if (!doc) { return; }
    doc.open(); doc.write("<!doctype html><html><head></head><body style=\"margin:0\"></body></html>"); doc.close();
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach((n) => doc.head.appendChild(n.cloneNode(true)));
    setBody(doc.body);
  }, []);
  const site = { name: t.name, theme: t.theme, pages: [{ id: "home", slug: "home", title: "Home", blocks: t.home || [] }] };
  return (
    <div ref={ref} className="relative aspect-[4/3] w-full overflow-hidden rounded-t-md bg-white" data-testid={`template-thumb-${t.id}`}>
      <iframe ref={frame} title={`${t.name} thumbnail`} tabIndex={-1} aria-hidden="true" className="pointer-events-none absolute left-0 top-0 origin-top-left border-0" style={{ width, height: width * 0.75, transform: `scale(${scale})` }} />
      {body && createPortal(<SiteRenderer site={site} thumb />, body)}
    </div>
  );
}

function PreviewModal({ id, onClose, onChoose }) {
  const [t, setT] = useState(null);
  const [page, setPage] = useState("home");
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    api.get(`/sites/templates/${id}`).then((r) => { setT(r.data); setPage(r.data.pages?.[0]?.slug); }).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [id, onClose]);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") { onClose(); } };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", k);
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = prev; };
  }, [onClose]);
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Template preview" className="fixed inset-0 z-[100] flex flex-col bg-black/80 backdrop-blur-sm" data-testid="template-preview-modal">
      <div className="flex flex-wrap items-center gap-3 border-b border-white/10 bg-[#0B0F17] px-4 py-3">
        <div className="min-w-0 flex-1"><div className="truncate font-medium">{t?.name || "Loading…"}</div><div className="text-xs text-slate-400">{t?.category}</div></div>
        <div className="flex rounded-md border border-white/10 p-0.5">
          <button onClick={() => setMobile(false)} className={`rounded px-2.5 py-1.5 ${!mobile ? "bg-white/10" : ""}`} aria-label="Desktop view" data-testid="template-preview-desktop"><Monitor className="h-4 w-4" /></button>
          <button onClick={() => setMobile(true)} className={`rounded px-2.5 py-1.5 ${mobile ? "bg-white/10" : ""}`} aria-label="Mobile view" data-testid="template-preview-mobile"><Smartphone className="h-4 w-4" /></button>
        </div>
        {onChoose && <Button onClick={() => onChoose(t)} disabled={!t} data-testid="template-preview-choose">Use this template</Button>}
        <button onClick={onClose} aria-label="Close preview" className="p-2 opacity-70 hover:opacity-100" data-testid="template-preview-close"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {t && <div className={`mx-auto overflow-hidden rounded-md bg-white shadow-2xl transition-[max-width] duration-300 ${mobile ? "max-w-[390px]" : "max-w-6xl"}`}>
          <SiteRenderer site={t} pageSlug={page} mobile={mobile} onNavigate={setPage} />
        </div>}
      </div>
    </div>, document.body
  );
}

function Card({ t, lab, onPreview, onChoose, onEdit, onRelease, onDelete }) {
  return (
    <div className="group flex flex-col overflow-hidden rounded-md border bg-card transition-colors hover:border-[#C2A462]/50" data-testid={`template-card-${t.id}`}>
      <div role="button" tabIndex={0} onClick={onPreview} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPreview(); } }} className="relative block cursor-pointer text-left" aria-label={`Preview ${t.name}`}>
        <TemplateThumb t={t} />
        <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-[opacity,background-color] duration-300 group-hover:bg-black/40 group-hover:opacity-100"><span className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-900"><Eye className="h-4 w-4" />Preview</span></span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-start gap-2"><div className="min-w-0 flex-1 font-medium">{t.name}</div>
          {lab && <Pill tone={t.released_all || t.released_to?.length ? "green" : "slate"}>{t.released_all ? "Released to all" : t.released_to?.length ? `Released to ${t.released_to.length}` : "Not released"}</Pill>}</div>
        <div className="mt-0.5 text-xs text-[#C2A462]">{t.category || "Other"} · {t.pages} {t.pages === 1 ? "page" : "pages"}</div>
        <p className="mt-2 flex-1 text-sm text-muted-foreground">{t.description}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onPreview} data-testid={`template-preview-${t.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Preview</Button>
          {lab ? <>
            <Button size="sm" variant="secondary" onClick={onEdit} data-testid={`site-edit-${t.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
            <Button size="sm" variant="outline" onClick={onRelease} data-testid={`site-release-${t.id}`}>Release</Button>
            <Button size="sm" variant="ghost" onClick={onDelete} aria-label="Delete template" data-testid={`site-delete-${t.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
          </> : <Button size="sm" onClick={onChoose} data-testid={`template-choose-${t.id}`}>Choose</Button>}
        </div>
      </div>
    </div>
  );
}

function CatBtn({ c, label, n, cat, setCat }) {
  return <button onClick={() => setCat(c)} className={`flex shrink-0 items-center justify-between gap-3 whitespace-nowrap rounded px-2.5 py-1.5 text-left text-sm transition-colors lg:w-full ${cat === c ? "bg-[#C2A462]/15 text-[#EBD6A0]" : "text-slate-300 hover:bg-white/5"}`} data-testid={`template-cat-${c ? c.toLowerCase().replace(/[^a-z]+/g, "-") : "all"}`}><span className="truncate">{label}</span><span className="text-xs text-slate-500">{n}</span></button>;
}

export function TemplateGallery({ templates, lab, onChoose, onEdit, onRelease, onDelete }) {
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);
  const counts = useMemo(() => Object.fromEntries(CATEGORIES.map((c) => [c, templates.filter((t) => (t.category || "Other") === c).length])), [templates]);
  const shown = templates.filter((t) => (!cat || (t.category || "Other") === cat) && (!q || `${t.name} ${t.description}`.toLowerCase().includes(q.toLowerCase())));
  const closePreview = useMemo(() => () => setPreview(null), []);
  const cats = CATEGORIES.filter((c) => lab || counts[c] > 0);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[220px_minmax(0,1fr)]" data-testid="template-gallery">
      <aside className="min-w-0 space-y-4">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates" className="pl-8" data-testid="template-search" /></div>
        <div><div className="overline mb-2">Browse categories</div>
          <div className="flex gap-1 overflow-x-auto lg:block lg:space-y-0.5">
            <CatBtn c="" label="All templates" n={templates.length} cat={cat} setCat={setCat} />
            {cats.map((c) => <CatBtn key={c} c={c} label={c} n={counts[c]} cat={cat} setCat={setCat} />)}
          </div></div>
      </aside>
      <div className="min-w-0">
        <div className="mb-4 text-sm text-slate-400" data-testid="template-count">Showing {shown.length} {shown.length === 1 ? "template" : "templates"}{cat ? ` in ${cat}` : ""}</div>
        <div className="grid gap-5 sm:grid-cols-2 2xl:grid-cols-3">
          {shown.map((t) => <Card key={t.id} t={t} lab={lab} onPreview={() => setPreview(t.id)} onChoose={() => onChoose(t)} onEdit={() => onEdit(t)} onRelease={() => onRelease(t)} onDelete={() => onDelete(t)} />)}
        </div>
        {!shown.length && <p className="text-sm text-muted-foreground" data-testid="template-empty">No templates match. Try another category or search.</p>}
      </div>
      {preview && <PreviewModal id={preview} onClose={closePreview} onChoose={lab ? null : (t) => { setPreview(null); onChoose(t); }} />}
    </div>
  );
}
